import BottomSheet from '@components/BottomSheet'
import { formatMoney } from '@plug-core/utils/formatNumber'
import { Button, Heading, Surface, Text, cn } from '@ui'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HiShieldCheck } from 'react-icons/hi'
import { HiArrowTrendingUp } from 'react-icons/hi2'
import { IoWarning } from 'react-icons/io5'

// Status tones: low = danger, medium = pending (warning), high = success.
const HEALTH_TEXT = {
  low: 'text-danger',
  medium: 'text-warning',
  high: 'text-success',
}

const HEALTH_BAR = {
  low: 'bg-danger',
  medium: 'bg-warning',
  high: 'bg-success',
}

const HEALTH_SOFT_BG = {
  low: 'bg-danger-bg',
  medium: 'bg-warning-bg',
  high: 'bg-success-bg',
}

const HEALTH_FACTOR_ICONS = {
  low: IoWarning,
  medium: HiArrowTrendingUp,
  high: HiShieldCheck,
}

const MAX_HEALTH_FACTOR = 5

type HealthFactorType = 'low' | 'medium' | 'high'

interface PortfolioProps {
  balances: {
    supplied: number
    borrowed: number
  }
  healthFactor: number | undefined
  newHealthFactor: number | undefined
}

const Portfolio: React.FC<PortfolioProps> = ({ balances, healthFactor, newHealthFactor }) => {
  const { t } = useTranslation()

  const [healthModalVisible, setHealthModalVisible] = useState(false)

  // An undefined health factor means there is no debt, which is maximum safety rather than zero.
  const toPortfolioHealth = (factor: number | undefined) =>
    factor === undefined ? 100 : Math.min((factor / MAX_HEALTH_FACTOR) * 100, 100)

  const portfolioHealth = toPortfolioHealth(healthFactor)
  const newPortfolioHealth = toPortfolioHealth(newHealthFactor)

  const getHealthFactorType = (percent: number): HealthFactorType =>
    percent > 70 ? 'high' : percent >= 50 ? 'medium' : 'low'

  const healthType = getHealthFactorType(portfolioHealth || 0)
  const newHealthType = getHealthFactorType(newPortfolioHealth)

  const clampPercent = (value: number) => `${Math.max(0, Math.min(100, value))}%`

  const renderHealthDescriptionRow = (percent: number) => {
    const healthFactorType = getHealthFactorType(percent)
    const Icon = HEALTH_FACTOR_ICONS[healthFactorType]

    return (
      <div
        key={percent}
        className={cn(
          'flex items-center rounded-sm p-3 transition-transform duration-200 hover:scale-[1.02] motion-reduce:hover:scale-100',
          HEALTH_SOFT_BG[healthFactorType],
          HEALTH_TEXT[healthFactorType]
        )}
      >
        <Icon aria-hidden="true" />

        <Text variant="body-s" weight="semibold" className="ml-4 flex-1">
          {percent}% Health
        </Text>

        <Text as="span" className="text-right text-[13px]">
          {t(`borrowLend.healthPositionDescriptions.${healthFactorType}`)}
        </Text>
      </div>
    )
  }

  return (
    <Surface padding="md">
      <div className="cursor-pointer" onClick={() => setHealthModalVisible(true)}>
        <div className="flex flex-col gap-2">
          <div
            className="flex cursor-pointer items-center gap-2"
            onClick={() => setHealthModalVisible(true)}
          >
            <Heading level={4}>{t('borrowLend.portfolioHealth.title')}</Heading>

            <Heading level={4} tabular className={cn('shrink-0', HEALTH_TEXT[healthType])}>
              {portfolioHealth.toFixed(0)}%
            </Heading>

            {/* Buffer bar: solid to the lower value, faint to the higher (projected) one. */}
            <div
              role="progressbar"
              aria-label={t('borrowLend.portfolioHealth.title')}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(portfolioHealth)}
              className="relative h-[7px] flex-1 overflow-hidden rounded-pill bg-hairline"
            >
              <div
                className={cn(
                  'absolute inset-y-0 left-0 rounded-pill opacity-35 transition-[width] duration-300 ease-out',
                  HEALTH_BAR[newHealthType]
                )}
                style={{ width: clampPercent(Math.max(portfolioHealth, newPortfolioHealth)) }}
              />
              <div
                className={cn(
                  'absolute inset-y-0 left-0 rounded-pill transition-[width] duration-300 ease-out',
                  HEALTH_BAR[healthType]
                )}
                style={{ width: clampPercent(Math.min(portfolioHealth, newPortfolioHealth)) }}
              />
            </div>
          </div>

          <div className="flex items-stretch gap-4">
            <div className="flex flex-1 flex-col gap-1">
              <Text variant="body-s" tone="fg-2">
                Lending
              </Text>
              <Text className="text-[18px] font-medium" tabular>
                {formatMoney(balances.supplied)}
              </Text>
            </div>

            <div className="flex flex-1 flex-col gap-1">
              <Text variant="body-s" tone="fg-2" align="end">
                Borrowing
              </Text>
              <Text className="text-[18px] font-medium" align="end" tabular>
                {formatMoney(balances.borrowed)}
              </Text>
            </div>
          </div>
        </div>
      </div>

      <BottomSheet
        visible={healthModalVisible}
        onBackdropClick={() => setHealthModalVisible(false)}
      >
        <div className="flex flex-col gap-4 p-2">
          <Heading level={3} align="center">
            {t('borrowLend.portfolioHealth.title')}
          </Heading>

          <Text variant="body-s" tone="fg-2" align="center">
            {t('borrowLend.portfolioHealth.description')}
          </Text>

          <div className="flex flex-col gap-3">
            {[0, 50, 100].map((percent) => renderHealthDescriptionRow(percent))}
          </div>

          <Text variant="caption" tone="fg-2" className="mt-2">
            {t('borrowLend.portfolioHealth.liquidationDescription')}
          </Text>

          <Button
            fullWidth
            variant="secondary"
            className="mt-2"
            onClick={() => setHealthModalVisible(false)}
          >
            Understood
          </Button>
        </div>
      </BottomSheet>
    </Surface>
  )
}

export default Portfolio
